// src/contexts/AuthContext.jsx
import React, { createContext, useState, useContext, useEffect } from "react";
import apiService from "../services/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("axoaccessToken");
    if (!token) {
      setLoading(false);
      return;
    }

    // Don't attempt auth check on public pages
    const publicPaths = ["/reset-password", "/forgot-password", "/accept-invite"];
    const isPublicPath = publicPaths.some(p => window.location.pathname.startsWith(p));
    if (isPublicPath) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiService.getCurrentUser();
      setUser(response.user);
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("axoaccessToken");
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await apiService.login(email, password);
    setUser(response.user);
    return response;
  };

  /**
   * Called by OnboardingSuccess after Stripe redirect.
   * Stores the JWT returned from /confirm-session and sets user state
   * directly from the token payload, no extra API call needed.
   */
  const loginWithToken = (token) => {
    // Store under the same key the rest of the app uses
    localStorage.setItem("axoaccessToken", token);

    // Decode the payload (backend already verified it, no need to re-verify)
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setUser({
        id:           payload.id,
        email:        payload.email,
        name:         payload.name,
        role:         payload.role,
        customerId:   payload.customerId,
        isSuperAdmin: payload.isSuperAdmin ?? false,
      });
    } catch (err) {
      // Malformed token, fall back to a full auth check
      console.error("loginWithToken: failed to decode payload, falling back to checkAuth", err);
      checkAuth();
    }
  };

  const logout = async () => {
    await apiService.logout();
    setUser(null);
  };

  const hasCustomer = () => {
    return !!(user?.customerId || user?.isSuperAdmin);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        loginWithToken,  // ← new
        logout,
        hasCustomer,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};